import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../../firebase.config";
import { User, Mail, Calendar, Shield, Camera, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AscendraPlusModal from "../../components/DialogueFlowMessenger/AscendraPlusModal";
import { getTimezoneInfo } from "../../utils/timezone";

export default function Profile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [diffDays, setDiffDays] = useState(null);
  const [isSelectedPlan, setIsSelectedPlan] = useState(false);
  const [showAscendraModal, setShowAscendraModal] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          toast.error("Please log in to view your profile");
          window.location.href = "/login";
          return;
        }

        const userRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(userRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserData(data);
          setProfileImage(data.profileImage || null);
          setFirstName(data.firstName || "");
          setLastName(data.lastName || "");
          setHasActiveSubscription(data.subscription_status || false);
          if (!data.profileImage) {
            toast("Please upload a profile image for a better experience.", {
              icon: "📸",
              duration: 3000,
            });
          }
        } else {
          toast.error("User data not found");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Failed to load profile data");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // Get diffDays and isSelectedPlan from localStorage
  useEffect(() => {
    const storedDiffDays = localStorage.getItem("diffDays");
    const storedIsSelectedPlan =
      localStorage.getItem("isSelectedPlan") === "true";
    if (storedDiffDays) {
      setDiffDays(Number(storedDiffDays));
      setIsSelectedPlan(storedIsSelectedPlan);
    }
  }, []);

  const processImageToCircularPNG = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();

        img.onload = () => {
          // Create a canvas element
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          // Determine the size (use the smaller dimension for a square)
          const size = Math.min(img.width, img.height);
          canvas.width = size;
          canvas.height = size;

          // Calculate crop position to center the image
          const startX = (img.width - size) / 2;
          const startY = (img.height - size) / 2;

          // Create circular clipping path
          ctx.beginPath();
          ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();

          // Draw the image centered and cropped
          ctx.drawImage(
            img,
            startX,
            startY,
            size,
            size, // Source rectangle
            0,
            0,
            size,
            size // Destination rectangle
          );

          // Convert canvas to blob (PNG format)
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error("Failed to create blob from canvas"));
              }
            },
            "image/png",
            1.0 // Maximum quality
          );
        };

        img.onerror = () => {
          reject(new Error("Failed to load image"));
        };

        img.src = e.target.result;
      };

      reader.onerror = () => {
        reject(new Error("Failed to read file"));
      };

      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setUploadingImage(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error("Please log in to upload image");
        setUploadingImage(false);
        return;
      }

      // Process image to circular PNG
      toast.loading("Processing image...", { id: "processing" });
      const processedBlob = await processImageToCircularPNG(file);
      toast.dismiss("processing");

      // Create a new file from the blob
      const processedFile = new File(
        [processedBlob],
        `profile_${Date.now()}.png`,
        { type: "image/png" }
      );

      // Upload to Firebase Storage
      toast.loading("Uploading image...", { id: "uploading" });
      const storageRef = ref(
        storage,
        `profile-images/${user.uid}/${processedFile.name}`
      );
      await uploadBytes(storageRef, processedFile);
      const downloadURL = await getDownloadURL(storageRef);
      toast.dismiss("uploading");

      // Update Firestore
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        profileImage: downloadURL,
        updatedAt: new Date(),
      });

      setProfileImage(downloadURL);
      setUserData((prev) => ({ ...prev, profileImage: downloadURL }));
      toast.success("Profile image updated successfully!");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image. Please try again.");
    } finally {
      setUploadingImage(false);
      toast.dismiss("processing");
      toast.dismiss("uploading");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!firstName || firstName.trim().length < 2) {
      newErrors.firstName = "First name must be at least 2 characters";
    }

    if (!lastName || lastName.trim().length < 1) {
      newErrors.lastName = "Last name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveChanges = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error("Please log in to update your profile");
        return;
      }
      const tzInfo = getTimezoneInfo();

      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        updatedAt: new Date(),
        ...tzInfo,
      });

      setUserData((prev) => ({
        ...prev,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        ...tzInfo,
      }));

      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-gray-50"
        style={{
          fontFamily:
            'system-ui, -apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif',
          background: "radial-gradient(circle at top, #111827 0, #020617 55%)",
          WebkitFontSmoothing: "antialiased",
        }}
      >
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-16 w-16 mx-auto border-4 border-slate-800 border-t-emerald-500"
            style={{
              boxShadow: "0 0 40px rgba(249, 115, 22, 0.4)",
            }}
          ></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="min-h-screen text-gray-50"
        style={{
          fontFamily:
            'system-ui, -apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif',
          background: "radial-gradient(circle at top, #111827 0, #020617 55%)",
          WebkitFontSmoothing: "antialiased",
          lineHeight: 1.7,
        }}
      >
        <section className="py-8">
          <div className="max-w-[580px] mx-auto px-4">
            <header className="mb-8 text-center">
              <h1 className="text-4xl  text-white font-poppins font-medium">
                My Profile
              </h1>
              <p className="text-xl text-gray-400 mt-2 font-poppins font-normal">
                Manage your account information
              </p>
            </header>

            <div
              className="border shadow-2xl"
              style={{
                padding: "32px 24px",
                borderRadius: "18px",
                background: "rgba(15, 23, 42, 0.7)",
                borderColor: "rgba(148, 163, 184, 0.24)",
                boxShadow: "0 24px 60px rgba(15, 23, 42, 0.7)",
              }}
            >
              {/* Profile Image Section */}
              <div className="flex flex-col items-center mb-8">
                <div className="relative">
                  <div className="w-32 h-32 rounded-full overflow-hidden bg-slate-800 flex items-center justify-center border-4 border-slate-700">
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User size={48} className="text-gray-500" />
                    )}
                  </div>
                  <label
                    htmlFor="profile-image"
                    className="absolute bottom-0 right-0 bg-orange-500 text-gray-900 p-2.5 rounded-full cursor-pointer hover:bg-orange-400 transition shadow-lg"
                  >
                    <Camera size={20} />
                    <input
                      id="profile-image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                  </label>
                </div>
                {uploadingImage && (
                  <p className="text-sm text-gray-400 mt-2">
                    Processing and uploading...
                  </p>
                )}
              </div>

              {/* Editable Name Fields */}
              <div className="space-y-5 mb-6">
                <div className="flex flex-col">
                  <label
                    htmlFor="firstName"
                    className="text-gray-400 text-sm mb-2"
                  >
                    First Name
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="px-4 py-3 rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="First Name"
                    style={{
                      background: "rgba(15, 23, 42, 0.9)",
                      borderColor: errors.firstName
                        ? "#ef4444"
                        : "rgba(148, 163, 184, 0.3)",
                      color: "#e5e7eb",
                    }}
                  />
                  {errors.firstName && (
                    <span className="text-red-400 text-sm mt-1.5">
                      {errors.firstName}
                    </span>
                  )}
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="lastName"
                    className="text-gray-400 text-sm mb-2"
                  >
                    Last Name
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="px-4 py-3 rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Last Name"
                    style={{
                      background: "rgba(15, 23, 42, 0.9)",
                      borderColor: errors.lastName
                        ? "#ef4444"
                        : "rgba(148, 163, 184, 0.3)",
                      color: "#e5e7eb",
                    }}
                  />
                  {errors.lastName && (
                    <span className="text-red-400 text-sm mt-1.5">
                      {errors.lastName}
                    </span>
                  )}
                </div>

                <button
                  onClick={handleSaveChanges}
                  disabled={saving}
                  className="w-full inline-flex items-center justify-center rounded-full border-0 cursor-pointer whitespace-nowrap transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                  style={{
                    padding: "12px 22px",
                    background: saving
                      ? "#9ca3af"
                      : "linear-gradient(135deg, #f97316, #fb923c)",
                    color: "#0b1120",
                    fontSize: "0.98rem",
                    boxShadow: saving
                      ? "none"
                      : "0 16px 40px rgba(249, 115, 22, 0.55)",
                  }}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>

              {/* Divider */}
              <div className="relative my-8 mb-12">
                <div className="absolute inset-0 flex items-center">
                  <div
                    className="w-full border-t"
                    style={{ borderColor: "rgba(148, 163, 184, 0.3)" }}
                  ></div>
                </div>
              </div>

              {/* View-Only Information */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-300">
                  Account Information
                </h3>

                {/* Email - always shown */}
                <div className="flex items-start">
                  <Mail className="mr-3 mt-1 text-gray-500" size={20} />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-base text-gray-200">
                      {userData?.email || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Current Membership - conditional based on subscription */}
                <div className="flex items-start">
                  <Shield className="mr-3 mt-1 text-gray-500" size={20} />
                  <div>
                    <p className="text-sm text-gray-500">Current Membership</p>
                    {diffDays >= 42 && !isSelectedPlan ? (
                      <p className="text-base text-gray-200">Ascendra Core</p>
                    ) : (
                      <p className="text-base text-gray-200 flex items-center gap-2">
                        <span>Ascendra Plus</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Member Since or Join Button - conditional */}
                {diffDays >= 42 && !isSelectedPlan ? (
                  <div className="mt-4">
                    <button
                      onClick={() => setShowAscendraModal(true)}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full border-0 cursor-pointer whitespace-nowrap transition-all font-semibold"
                      style={{
                        padding: "12px 22px",
                        background: "linear-gradient(135deg, #f97316, #fb923c)",
                        color: "#0b1120",
                        fontSize: "0.98rem",
                        boxShadow: "0 16px 40px rgba(249, 115, 22, 0.55)",
                      }}
                    >
                      <Star size={18} />
                      Join Ascendra Plus
                    </button>
                  </div>
                ) : (
                  <div className="flex items-start">
                    <Calendar className="mr-3 mt-1 text-gray-500" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">Member Since</p>
                      <p className="text-base text-gray-200">
                        {userData?.createdAt
                          ? new Date(
                              userData.createdAt.seconds * 1000
                            ).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Back to Dashboard */}
            <div className="text-center mt-8">
              <button
                onClick={() => (window.location.href = "/dashboard")}
                className="text-orange-400 font-medium hover:text-orange-300 transition-colors text-base"
              >
                Return To Main Screen →
              </button>
            </div>
          </div>
        </section>
      </div>
      {/* Ascendra Plus Modal */}
      <AscendraPlusModal
        showPayment={showAscendraModal}
        setShowPayment={setShowAscendraModal}
      />
    </>
  );
}
